import {
  type NarrowFieldsByValueType,
  TidalFormState,
  FormFieldDescriptor,
  TidalFormBridgeOptions,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'
import {
  SimpleSelect as TidalSimpleSelect,
  type SimpleSelectOption,
  type SimpleSelectProps,
} from '@workwave-tidal/core/components/SimpleSelect'

import {
  FormHelperText,
  FormControlLabelProps,
  FormControl,
  SelectChangeEvent,
} from '@mui/material'

import { BulkFieldValue } from './types'
import { useCallback } from 'react'

export type BulkSelectValue = BulkFieldValue<string>
const defaultComponentValue = {
  status: 'mixed',
}

const mixedValue = 'mixed'

type Option = SimpleSelectOption<string>

export type BulkSelectProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkSelectValue,
> = {
  formControlLabelProps?: Partial<FormControlLabelProps>
  simpleSelectProps?: Partial<Omit<SimpleSelectProps<string>, 'options' | 'value' | 'onChange'>>
  options: Option[]
  // label to use in case of mixed value
  mixedLabel: string
} & TidalFormBridgeOptions<BulkSelectValue, TargetField, State, AcceptedFormValue>

export function BulkSelect<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkSelectValue,
>(props: BulkSelectProps<TargetField, State, AcceptedFormValue>) {
  const {
    simpleSelectProps = {},
    formControlLabelProps = {},
    mixedLabel,
    options: userOptions,
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    config: { fullWidth, label, name, testId = 'checkbox-root' },
    fieldInfo: {
      errorOrHelperText,
      field: { visible, required },
      fieldDisabled,
      fieldHasError,
      onBlur,
      onChange,
      onFocus,
      showHelperText,
      value,
    },
  } = useTidalFormBridge<BulkSelectValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onChange({ status: 'exact', value: event.target.value })
    },
    [onChange],
  )

  const options =
    value.status === 'mixed' ? getOptionsWithMixedValue(userOptions, mixedLabel) : userOptions

  if (!visible) return null

  return (
    <FormControl
      error={fieldHasError}
      component="fieldset"
      fullWidth={fullWidth}
      data-testid={testId}
      required={required}
      data-tidal-form-field-name={bridgeOptions.name}
      sx={{
        '&.tidal-form-devtool-highlight': theme => ({
          outline: `4px solid var(--tidal-devtools-highlight-color, ${theme.vars.palette.error.dark})`,
        }),
      }}
    >
      <TidalSimpleSelect
        // variant="outlined"
        labelId={name}
        label={label}
        required={required}
        value={value.status === 'mixed' ? mixedValue : value.value}
        onChange={handleOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={fieldDisabled}
        options={options}
      />

      {showHelperText && <FormHelperText error={fieldHasError}>{errorOrHelperText}</FormHelperText>}
    </FormControl>
  )
}

function getOptionsWithMixedValue(
  optionsWithoutMixedValue: Option[],
  mixedLabel: string,
): Option[] {
  const mixedOption = {
    value: mixedValue,
    label: mixedLabel,
    disabled: false,
  }

  return [mixedOption, ...optionsWithoutMixedValue]
}
